import React from 'react';
import { Button, Checkbox, Input, Select } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, PhoneOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { StatCard } from '../../../components/overview_page';
import { colors } from '../../../../domain/types/colors';
import type useReceptionistCallsTabController from '../controllers/useReceptionistCallsTabController';
import ReceptionistCallList from '../components/ReceptionistCallList';
import ReceptionistCallDetail from '../components/ReceptionistCallDetail';
import ReceptionistCallsCustomRange from '../components/ReceptionistCallsCustomRange';
import {
  RECEPTIONIST_CALLS_CONTENT_OPTIONS,
  RECEPTIONIST_CALLS_PERIOD_OPTIONS,
  formatReceptionistCallDuration,
} from '../types/ReceptionistCallDisplay';
import './ReceptionistCallsTab.css';

interface ReceptionistCallsTabProps {
  controller: ReturnType<typeof useReceptionistCallsTabController>;
}

// The controller lives in the page so the tab label can show the unread count
const ReceptionistCallsTab: React.FC<ReceptionistCallsTabProps> = ({ controller }) => {
  const { state, actions } = controller;
  const { totals, isLoading } = state;

  return (
    <div className="receptionist-calls">
      <div className="receptionist-ai-page__stats-row">
        <StatCard
          icon={<PhoneOutlined style={{ fontSize: 24, color: colors.primary.main }} />}
          label="APPELS PRIS EN CHARGE"
          value={String(totals.callsCount)}
          subtitle={`${totals.withContentCount} avec contenu · ${totals.withoutContentCount} sans contenu`}
          iconBgColor="green"
          loading={isLoading}
        />
        <StatCard
          icon={<ClockCircleOutlined style={{ fontSize: 24, color: colors.primary.main }} />}
          label="TEMPS PRIS EN CHARGE"
          value={formatReceptionistCallDuration(totals.durationMs)}
          subtitle="Durée cumulée des appels"
          iconBgColor="blue"
          loading={isLoading}
        />
        <StatCard
          icon={<CheckCircleOutlined style={{ fontSize: 24, color: colors.primary.main }} />}
          label="OPPORTUNITÉS IDENTIFIÉES"
          value={String(totals.opportunitiesCount)}
          subtitle="Nouveaux projets d'achat ou de vente"
          iconBgColor="green"
          loading={isLoading}
        />
      </div>

      <div className="receptionist-calls__filters">
        <Select
          className="receptionist-calls__filter"
          value={state.period}
          options={RECEPTIONIST_CALLS_PERIOD_OPTIONS}
          onChange={actions.setPeriod}
        />
        <Input
          className="receptionist-calls__search"
          prefix={<SearchOutlined />}
          placeholder="Rechercher un appel…"
          allowClear
          value={state.searchValue}
          onChange={(e) => actions.setSearchValue(e.target.value)}
        />
        <Select
          className="receptionist-calls__filter"
          value={state.contentFilter}
          options={RECEPTIONIST_CALLS_CONTENT_OPTIONS}
          onChange={actions.setContentFilter}
        />
        <Checkbox
          className="receptionist-calls__checkbox"
          checked={state.urgentOnly}
          onChange={(e) => actions.setUrgentOnly(e.target.checked)}
        >
          Urgents uniquement
        </Checkbox>
        {state.canFilterByUser && (
          <Select
            className="receptionist-calls__filter receptionist-calls__filter--users"
            mode="multiple"
            maxTagCount="responsive"
            suffixIcon={<TeamOutlined />}
            placeholder="Tous les utilisateurs"
            allowClear
            value={state.subscriptionFilter}
            options={state.userOptions}
            onChange={actions.setSubscriptionFilter}
          />
        )}
        {state.hasActiveFilters && (
          <Button type="link" onClick={actions.resetFilters}>
            Réinitialiser
          </Button>
        )}
      </div>

      {state.period === 'custom' && (
        <ReceptionistCallsCustomRange range={state.customRange} onApply={actions.setCustomRange} />
      )}

      <div className="receptionist-calls__split">
        <ReceptionistCallList
          groups={state.groups}
          callsCount={state.callsCount}
          selectedCallId={state.selectedCall?.id ?? null}
          isLoading={isLoading}
          isLoadingMore={state.isLoadingMore}
          onSelect={actions.selectCall}
        />
        <ReceptionistCallDetail call={isLoading ? null : state.selectedCall} />
      </div>
    </div>
  );
};

export default ReceptionistCallsTab;
